import { nanoid } from 'nanoid';
export const TOAST_TIMEOUT = 5000;
/**
 * Initializes a container map for a given store and container ID.
 * The container map is a Map object that stores toast definitions with their IDs.
 *
 * @param {ToastStore} store - The store object to initialize the container map for.
 * @param {ContainerId} containerId - The ID of the container to associate the map with.
 * @returns {Map<ToastId, ToastDefinition>} - The initialized container map.
 */
export const initializeContainerMap = (store, containerId) => {
  const containerMap = new Map();
  store.set(containerId, containerMap);
  return containerMap;
};

/**
 * Updates the toast state based on the provided action.
 *
 * @param {Map<string, Map<string, ToastDefinition>>} state - The current toast state.
 * @param {ToastAction} action - The action to be performed on the toast state.
 * @returns {Map<string, Map<string, ToastDefinition>>} The new toast state after the action is performed.
 */
export const toastReducer = (state, action) => {
  const containerMap = state.get(action.entry.containerId);
  switch (action.type) {
    case 'add_toast':
      if (containerMap) {
        const newContainerMap = new Map(containerMap).set(action.entry.toastId, action.entry);
        return new Map(state).set(action.entry.containerId, newContainerMap);
      }
      return state;
    case 'remove_toast':
      if (containerMap) {
        const newContainerMap = new Map(containerMap);
        newContainerMap.delete(action.entry.toastId);
        return new Map(state).set(action.entry.containerId, newContainerMap);
      }
      return state;
    default:
      return state;
  }
};

/**
 * Creates a new Toast store with a unique container ID.
 * This function initializes the store with an empty Map object,
 * sets a unique container ID using nanoid, and initializes
 * the container map with the given store and container ID.
 *
 * @returns {Object} An object containing the toast store and container ID.
 * @property {Map} IDS_TOAST_STORE - The toast store as a Map object.
 * @property {string} IDS_TOAST_CONTAINER_ID - The unique container ID.
 */
export const createStore = () => {
  const store = new Map();
  const containerId = nanoid();
  initializeContainerMap(store, containerId);
  return {
    IDS_TOAST_STORE: store,
    IDS_TOAST_CONTAINER_ID: containerId
  };
};
export const {
  IDS_TOAST_STORE,
  IDS_TOAST_CONTAINER_ID
} = createStore();